import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, ViewHeader, StatCard, MicroLabel, Empty, QueryError, Notice, Disclosure } from '../ui'
import { useDataFindings, useDataProjectHealth, useDepartments, useLaneTypes, useRuns, useUpstream, RUNS_WINDOW } from '../data/queries'
import { deskSummary, toDeskTickets, type DeskTicket } from '../data/map/desk'
import { toUpstreamLine } from '../data/map/upstream'
import { ticketRuns } from '../data/map/tickets'
import { departmentLabel } from '../data/map/department'
import { DeskFindings } from './DeskFindings'
import { DeskLinearTickets } from './DeskLinearTickets'
import { DeskAppUsage } from './DeskAppUsage'
import { ticketsByRepo, ticketsLine } from '../data/map/summaries'
import { usd } from '../lib/money'
import { age, duration } from '../lib/time'

/**
 * One department's desk: how the work it did performed, per ticket and in
 * aggregate.
 *
 * Generic and driven by the slug. It is useful for the development department
 * on day one because that is where the runs are, and it renders correctly and
 * sparsely for any other -- a slug the departments table does not know is a
 * real case here, the same as everywhere else, and reads as a desk under a
 * bare slug rather than an error.
 *
 * That case is kept distinct from a `useDepartments()` that failed. Both leave
 * the heading showing the slug, but one means there is no such department and
 * the other means we cannot currently tell, and a page that says the first
 * when the second is true is lying about the fleet.
 *
 * The other half of a desk -- what the department *found* -- is `DeskFindings`,
 * which reads `v_findings` for the same slug and renders nothing where there
 * is nothing. This half is arithmetic over rows already fetched, so it needed no
 * query and knows no department; that one has a query and still knows none.
 */

/**
 * A figure in the per-ticket table.
 *
 * `waste` takes the muted clay of the failure family rather than ink. Clay is
 * lower contrast than ink, not higher, so a ticket that went around twice reads
 * as slightly quieter than one that went around once -- which is the right way
 * round. There is no badge, no fill and no count styled as an alert: a
 * department having a bad week must not shout from this table.
 */
function Figure({ label, value, waste }: { label: string; value: string; waste?: boolean }) {
  return (
    <div className={`${css.num} mono`} style={waste ? { color: 'var(--s-fail)' } : undefined}>
      <span className={css.deskFigureLabel}>{label}</span>
      {value}
    </div>
  )
}

export function Desk() {
  const { slug = '' } = useParams<{ slug: string }>()
  const runs = useRuns(slug)
  const depts = useDepartments()
  // The same two queries DeskFindings makes, deduplicated by their keys. Read here
  // only to know whether the "no runs" card below would be the whole page or a
  // footnote under a department that does its work somewhere other than agent_runs.
  const findings = useDataFindings(slug)
  const projects = useDataProjectHealth(slug)
  const reportsFindings = (findings.data?.length ?? 0) > 0 || (projects.data?.length ?? 0) > 0

  // Report lanes' runs (data-dq) carry a project ref, not a ticket's, and are
  // left out of the ticket table; the department's findings say what they found.
  const lanes = useLaneTypes()
  const tickets = useMemo(
    () => toDeskTickets(ticketRuns(runs.data ?? [], lanes.data), slug),
    [runs.data, lanes.data, slug],
  )
  const summary = useMemo(() => deskSummary(tickets), [tickets])
  const groups = useMemo(() => ticketsByRepo(tickets), [tickets])

  // What has not started yet, which nothing else on this page can see. A read
  // still in flight, one that failed, or a department the fleet does not count
  // upstream work for all leave the line null and simply absent -- a desk that
  // never had it must look exactly as it did, not like a fault.
  const upstream = useUpstream(slug)
  const upstreamLine = useMemo(() => toUpstreamLine(upstream.data ?? []), [upstream.data])

  const label = departmentLabel(slug, depts.data ?? [])

  /**
   * Why the heading is a bare slug, when it is one. Two different facts wear
   * the same fallback -- `departmentLabel` returns the slug whether the table
   * said there is no such row or the table could not be read at all -- and
   * conflating them tells an operator a department does not exist when really
   * the list is unreachable. Nothing is said while the query is still in
   * flight, because neither fact is known yet.
   *
   * Stated, not flagged: no icon, no clay, no colour (product rule 1). It
   * explains a heading; a department list that is down is not an emergency
   * on a page whose figures come from `agent_runs` regardless.
   */
  const named = depts.data?.some((d) => d.slug === slug) ?? false
  // data is tested before error, and the order is the point. TanStack Query
  // keeps the last good data through a failed refetch, so on a 15s poll both
  // are set for a while. Reading error first would flip a settled "no such
  // slug" to a hedge and back on every transient failure. A list we have --
  // even one read a poll ago -- answers the question; only having no list at
  // all leaves it unanswered.
  const deptNote =
    named ? null
    : depts.data ? 'No department by this slug — the departments table has no row for it, so the heading is the slug as the fleet stores it.'
    : depts.error ? 'Department name unavailable — the departments table could not be read, so the heading is the slug. Whether a department by this slug exists is not known right now.'
    : null

  if (runs.error) return <QueryError error={runs.error} />
  if (!runs.data) return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading runs…</div></Card>

  const header = (
    <ViewHeader
      title={label}
      aside={
        // The nav used to point here, and the filter it pointed at is still how
        // the inbox is narrowed. Keeping one link to it means moving the entry
        // to the desk costs nobody the view they had.
        <Link to={`/inbox?dept=${encodeURIComponent(slug)}`}>Inbox, filtered to this department →</Link>
      }
    />
  )

  const note = deptNote ? <p className={css.deskDeptNote}>{deptNote}</p> : null

  /**
   * The upstream line: what is waiting in Linear, above everything the desk
   * measures, because that is the order it is read in -- what is coming, then
   * what performed.
   *
   * A sentence, not a figure. Product rule 1 names the count badge specifically,
   * and "14 in Backlog" beside a spend card, boxed and labelled, is the pressure
   * shape that rule exists to stop. So: the note's own typography, one shade
   * darker than the heading's footnote because this is about the department
   * rather than about the page.
   */
  const upstreamNote = upstreamLine ? (
    <p className={css.deskDeptNote} style={{ color: 'var(--ink-2)' }}>{upstreamLine}</p>
  ) : null

  if (tickets.length === 0) {
    return (
      <>
        {header}
        {note}
        {upstreamNote}
        <DeskFindings slug={slug} />
        <DeskLinearTickets slug={slug} />
        <DeskAppUsage slug={slug} />
        {reportsFindings ? null : (
          <Card>
            <Empty glyph="[ ]">
              No runs from this department in the window. A desk fills itself the first time one of
              the department's lanes finishes a run — nothing needs to be configured here for that
              to happen.
            </Empty>
          </Card>
        )}
      </>
    )
  }

  const timed = summary.wallClockTickets
  const agentTimed = summary.agentTimedTickets

  return (
    <>
      {header}
      {note}
      {upstreamNote}
      <DeskFindings slug={slug} />
      <DeskLinearTickets slug={slug} />
      <DeskAppUsage slug={slug} />

      <div className={css.deskStats}>
        <StatCard
          label="Spend"
          value={summary.hasCost ? usd(summary.spend, { precise: summary.spend < 1 }) : '—'}
          note={
            summary.hasCost
              ? `across ${summary.tickets} ${summary.tickets === 1 ? 'ticket' : 'tickets'}`
              : 'no run here recorded a cost'
          }
        />
        <StatCard
          label="Builds"
          value={String(summary.builds)}
          note={
            summary.retrips === 0
              ? 'each one reached a branch'
              : `${summary.retrips} ${summary.retrips === 1 ? 'retrip' : 'retrips'} among them`
          }
        />
        <StatCard
          label="Respecs"
          value={String(summary.respecs)}
          note="completed spec runs beyond the first"
        />
        {/* The two clocks sit side by side and are never added. One is how long
            the agents worked; the other is how long the ticket took, most of
            which is usually waiting for a person.

            Three cards rather than two, because a total beside a median is not
            a comparison anyone can make. The two medians are the comparable
            pair -- agent time inside the ticket it was spent on -- and that
            pair is the answer to how long it takes to build something. */}
        <StatCard
          label="Agent time"
          value={duration(summary.agentSeconds)}
          note="summed across runs"
        />
        <StatCard
          label="Agent time, median ticket"
          value={duration(summary.medianAgentSeconds)}
          note={
            agentTimed === 0
              ? 'nothing has finished yet'
              : `over ${agentTimed} ${agentTimed === 1 ? 'ticket' : 'tickets'}`
          }
        />
        <StatCard
          label="Wall clock, median ticket"
          value={duration(summary.medianWallClockSeconds)}
          note={timed === 0 ? 'nothing has finished yet' : `over ${timed} ${timed === 1 ? 'ticket' : 'tickets'}`}
        />
      </div>

      <Card style={{ marginBottom: 14 }}>
        <div className={css.deskPhaseHead}>
          <MicroLabel>Spend by part of the loop</MicroLabel>
          <span className={css.ticketHeadNote}>cost_usd only — no token figure is in these totals</span>
        </div>
        <div className={css.deskPhases}>
          {([
            ['Specification', summary.cost.spec],
            ['Build', summary.cost.build],
            ['Review', summary.cost.review],
            ['Other lanes', summary.cost.other],
          ] as const)
            // A part of the loop this department does not have reads as absent
            // rather than as free.
            .filter(([, amount]) => amount > 0)
            .map(([name, amount]) => (
              <div key={name} className={css.deskPhase}>
                <MicroLabel>{name}</MicroLabel>
                <div className={`${css.deskPhaseValue} mono`}>{usd(amount, { precise: amount < 1 })}</div>
              </div>
            ))}
        </div>
      </Card>

      {/* One section per repo, which is one per Linear project through the
          repo routing. Each closes to a line of its own totals; the cards
          above total them all. A desk with one repo opens it. */}
      {groups.map((group) => (
        <Card key={group.repo ?? ''} style={{ marginBottom: 14 }}>
          <Disclosure
            title={group.name}
            summary={[group.owner, ticketsLine(group.tickets)].filter(Boolean).join(' · ')}
            defaultOpen={groups.length === 1}
          >
            <TicketTable tickets={group.tickets} />
          </Disclosure>
        </Card>
      ))}

      <Notice>
        <span style={{ padding: '0 18px', display: 'block' }}>
          Agent time is run time summed; wall clock is the first run created to the last one
          ended, most of which is usually waiting for a person. They measure different things
          and are never added. A build is a run that reached a branch, so a claim the staleness
          check cancelled is not counted as one — it did no work. Respecs undercount where the
          original specification was written by hand, because the redraft is then the ticket's
          only spec run. And cost is what each run row recorded, which includes work a retried
          run threw away, so it is spend rather than productive spend; both of those need the
          events table to say more. This reads the department's {RUNS_WINDOW} most recent runs, so it is a
          window on how the department is working now rather than a full history. Tickets are grouped
          by the repo their runs went to, which the dispatcher picks from the ticket's Linear project,
          so each section is one project; a ticket whose runs carry no repo is listed on its own.
        </span>
      </Notice>
    </>
  )
}

/** One repo's tickets: the column heads, then a row per ticket. */
function TicketTable({ tickets }: { tickets: ReadonlyArray<DeskTicket> }) {
  return (
    <>
      <div className={css.deskHead}>
        <MicroLabel>Ticket</MicroLabel>
        <MicroLabel>Work</MicroLabel>
        <MicroLabel className={css.num}>Builds</MicroLabel>
        <MicroLabel className={css.num}>Retrips</MicroLabel>
        <MicroLabel className={css.num}>Respecs</MicroLabel>
        <MicroLabel className={css.num}>Agent</MicroLabel>
        <MicroLabel className={css.num}>Wall</MicroLabel>
        <MicroLabel className={css.num}>Spend</MicroLabel>
      </div>

      {tickets.map((ticket) => {
        const split = ([
          ['spec', ticket.cost.spec],
          ['build', ticket.cost.build],
          ['review', ticket.cost.review],
          ['other', ticket.cost.other],
        ] as const).filter(([, amount]) => amount > 0)

        return (
          <div key={ticket.ref} className={css.deskRow}>
            <div>
              <span className="mono" style={{ fontSize: 12 }}>
                {ticket.url ? (
                  <a href={ticket.url} target="_blank" rel="noreferrer">{ticket.ref}</a>
                ) : (
                  ticket.ref
                )}
              </span>
              <div className={css.ticketAge}>{age(ticket.latestAt)}</div>
            </div>

            <div>
              <div className={css.ticketTitle}>{ticket.title ?? ticket.ref}</div>
              <div className={`${css.ticketLanes} mono`}>
                <span style={{ color: `var(--s-${ticket.stage.token})` }}>{ticket.stage.label}</span>
                <span>{ticket.runs} {ticket.runs === 1 ? 'run' : 'runs'}</span>
                {split.map(([name, amount]) => (
                  <span key={name}>{name} {usd(amount, { precise: amount < 1 })}</span>
                ))}
              </div>
              {ticket.failureSummary ? (
                <div className={css.ticketFail}>{ticket.failureSummary}</div>
              ) : null}
              {ticket.prUrls.length > 0 ? (
                <div className={css.ticketLinks}>
                  {ticket.prUrls.map((url, i) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      {ticket.prUrls.length === 1 ? 'Pull request' : `Pull request ${i + 1}`}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            <Figure label="Builds" value={String(ticket.builds)} />
            <Figure label="Retrips" value={String(ticket.retrips)} waste={ticket.retrips > 0} />
            <Figure label="Respecs" value={String(ticket.respecs)} waste={ticket.respecs > 0} />
            <Figure label="Agent" value={duration(ticket.agentSeconds)} />
            <Figure label="Wall" value={duration(ticket.wallClockSeconds)} />
            <Figure
              label="Spend"
              value={ticket.hasCost ? usd(ticket.spend, { precise: ticket.spend < 1 }) : '—'}
            />
          </div>
        )
      })}
    </>
  )
}
